import React from 'react';
import { Card } from '../ui';
import Pagination from '../ui/Pagination';

interface TablePageLayoutProps {
  title: string;
  description?: string;
  action?: React.ReactNode;
  filters?: React.ReactNode;
  search?: React.ReactNode;
  children: React.ReactNode;
  isLoading?: boolean;
  // Pagination Props
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  totalItems?: number;
  limit?: number;
  onLimitChange?: (limit: number) => void;
  // Legacy prop (optional, for backward compatibility or custom footer)
  pagination?: React.ReactNode;
}

export const TablePageLayout: React.FC<TablePageLayoutProps> = ({
  title,
  description,
  action,
  filters,
  search,
  children,
  isLoading: _isLoading = false,
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  limit,
  onLimitChange,
  pagination,
}) => {
  const showPagination = !!(currentPage && onPageChange) || !!(totalItems !== undefined && totalItems >= 0);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex shrink-0 flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
          {description && <p className="mt-1 text-slate-500">{description}</p>}
        </div>
        {action && <div>{action}</div>}
      </div>

      <Card className="border-slate-200 shadow-sm" noPadding>
        {/* Filter & Search Area */}
        {(filters || search) && (
          <div className="flex shrink-0 flex-col items-center justify-between gap-4 border-b border-slate-100 bg-white p-4 lg:flex-row">
            <div className="flex w-full items-center gap-3 lg:w-auto">{filters}</div>
            <div className="flex w-full items-center gap-2 lg:w-auto">{search}</div>
          </div>
        )}

        {/* Table Area */}
        <div className="relative overflow-x-auto">{children}</div>

        {/* Pagination Area */}
        {(showPagination || pagination) && (
          <div className="flex shrink-0 items-center justify-between gap-4 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
            {pagination ? (
              pagination
            ) : (
              <>
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  {totalItems !== undefined && limit && currentPage && (
                    <span className="whitespace-nowrap">
                      총 <span className="font-medium text-slate-900">{totalItems.toLocaleString()}</span>개 중{' '}
                      <span className="font-medium text-slate-900">
                        {((currentPage - 1) * limit + 1).toLocaleString()}
                      </span>
                      -
                      <span className="font-medium text-slate-900">
                        {Math.min(currentPage * limit, totalItems).toLocaleString()}
                      </span>{' '}
                      표시
                    </span>
                  )}

                  {limit && onLimitChange && (
                    <div className="flex items-center gap-2">
                      <select
                        value={limit}
                        onChange={(e) => onLimitChange(Number(e.target.value))}
                        className="form-select rounded-md border-slate-300 bg-white py-1 pr-8 pl-2 text-sm focus:border-indigo-500 focus:ring-indigo-500">
                        <option value={10}>10개씩 보기</option>
                        <option value={20}>20개씩 보기</option>
                        <option value={50}>50개씩 보기</option>
                        <option value={100}>100개씩 보기</option>
                      </select>
                    </div>
                  )}
                </div>

                {totalPages && totalPages > 1 && onPageChange && currentPage && (
                  <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
                )}
              </>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};
